import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { TrendingUp, Sparkles } from "lucide-react";

const TYPE_COLORS = {
  Reel: "from-pink-500 to-rose-500",
  Story: "from-violet-500 to-fuchsia-500",
  DM: "from-emerald-500 to-teal-500",
  Carousel: "from-amber-500 to-orange-500",
  Webinar: "from-blue-500 to-indigo-500",
  Funnel: "from-purple-500 to-pink-500",
};

export default function Attribution() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      const [s, c] = await Promise.all([api.get("/attribution/summary"), api.get("/attribution/content")]);
      setSummary(s.data); setRows(c.data);
    })();
  }, []);

  const apply = async (f) => {
    setFilter(f);
    const { data } = await api.get(`/attribution/content?type=${f}`);
    setRows(data);
  };

  if (!summary) return <div className="p-10 text-zinc-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="attribution-page">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="h-6 w-6 text-violet-600" />
        <div>
          <h1 className="font-display text-3xl font-bold">AI Content Attribution</h1>
          <p className="text-sm text-zinc-500 mt-1">Revenue tracked to the content that drove it.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-3xl bg-white border border-black/5 p-5">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Attributed revenue</p>
          <p className="mt-2 font-display text-3xl font-extrabold text-emerald-600">${Math.round(summary.total_revenue).toLocaleString()}</p>
        </div>
        <div className="rounded-3xl bg-white border border-black/5 p-5">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Total conversions</p>
          <p className="mt-2 font-display text-3xl font-extrabold text-violet-600">{summary.total_conversions}</p>
        </div>
        <div className="rounded-3xl bg-gradient-to-br from-violet-500/15 to-pink-500/10 border border-violet-200 p-5">
          <p className="text-[11px] uppercase tracking-wider text-violet-700 font-medium">Top performer</p>
          <p className="mt-2 font-display text-lg font-bold text-zinc-900 line-clamp-1">{summary.top_performers?.[0]?.title}</p>
          <p className="text-xs text-zinc-600">${Math.round(summary.top_performers?.[0]?.revenue || 0).toLocaleString()} · {summary.top_performers?.[0]?.type}</p>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-black/5 p-6 mb-4">
        <h3 className="font-display text-lg font-bold mb-4">Revenue by content type</h3>
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(summary.by_type).map(([t, v]) => (
            <div key={t} className="rounded-2xl border border-black/5 bg-zinc-50 p-4" data-testid={`type-${t}`}>
              <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${TYPE_COLORS[t] || "from-zinc-400 to-zinc-500"} mb-2`} />
              <p className="text-sm font-semibold">{t}</p>
              <p className="text-xs text-zinc-500">{v.count} pieces · {v.conversions} conv.</p>
              <p className="mt-1 font-display text-lg font-extrabold text-emerald-600">${Math.round(v.revenue).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4 inline-flex rounded-full bg-white border border-black/5 p-1 flex-wrap">
        {["all", "Reel", "Story", "DM", "Webinar", "Funnel", "Carousel"].map((f) => (
          <button key={f} onClick={() => apply(f)} data-testid={`filter-${f}`}
            className={`px-4 py-1.5 text-xs rounded-full ${filter === f ? "ashra-gradient-bg text-white" : "text-zinc-500"}`}>
            {f === "all" ? "All" : f}
          </button>
        ))}
      </div>

      <div className="rounded-3xl bg-white border border-black/5 p-6">
        <h3 className="font-display text-lg font-bold mb-4 inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> Content performance</h3>
        <div className="divide-y divide-black/5">
          {rows.map((r) => (
            <div key={r.id} className="py-3 grid grid-cols-12 gap-3 items-center text-sm" data-testid={`content-${r.content_id}`}>
              <div className="col-span-6">
                <p className="font-semibold line-clamp-1">{r.title}</p>
                <p className="text-xs text-zinc-500">{r.type} · {r.content_id}</p>
              </div>
              <div className="col-span-2 text-center">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Engagement</p>
                <p className="font-medium">{(r.engagement || 0).toLocaleString()}</p>
              </div>
              <div className="col-span-2 text-center">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Conversion</p>
                <p className="font-medium text-violet-600">{(r.conversion_rate * 100).toFixed(1)}%</p>
              </div>
              <div className="col-span-2 text-right">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Revenue</p>
                <p className="font-display text-lg font-extrabold text-emerald-600">${Math.round(r.revenue).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
